import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraIconButton, SpecraLink, SpecraRecordRail, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { format, parseISO } from 'date-fns';
import { Link2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { lifecycleLabel } from '../../CaptureLifecyclePanel';
import { GrowthHintControl } from '../GrowthHintControl';
import { RfpExpectedDialog } from '../RfpExpectedDialog';
const RAIL_TESTIDS = {
    pursuit: Testids.pursuitRail.pursuit,
    opportunity: Testids.pursuitRail.opportunity,
    people: Testids.pursuitRail.people,
    competitors: Testids.pursuitRail.competitors,
    related: Testids.pursuitRail.related
};
function ref(entity) {
    if (!entity?.label) return null;
    return entity.href ? /*#__PURE__*/ _jsx(SpecraLink, {
        href: entity.href,
        renderLink: renderRouterLink,
        children: entity.label
    }) : entity.label;
}
function refs(entities) {
    const list = (entities ?? []).filter((e)=>e.label);
    if (list.length === 0) return null;
    return /*#__PURE__*/ _jsx("span", {
        className: "inline-flex flex-wrap gap-x-[var(--space-2)]",
        children: list.map((e)=>/*#__PURE__*/ _jsx("span", {
                children: ref(e)
            }, `${e.kind}:${e.id}`))
    });
}
function monthYear(value) {
    return value ? format(parseISO(value), 'MMM yyyy') : '';
}
/** Facts with an empty value are dropped, so the rail never shows a bare label. */ function facts(rows) {
    return rows.filter((r)=>r.value !== null && r.value !== undefined && r.value !== '');
}
/**
 * The pursuit facts rail on Home (GRW-16 item 5). Every section is built from
 * the header read model; a section with nothing to show is omitted, except
 * `pursuit` (always) and `opportunity` (says when the pursuit was not opened
 * from one).
 *
 * Editors get the RFP-expected pencil (GRW-06 RfpExpectedDialog) when an
 * opportunity is linked, and "Link contract" (GRW-04, `onLinkContract` opens
 * the page's LinkRecompeteDialog) while no current contract is linked. With a
 * current contract, `related` ends with the GRW-04 Growth-hint switch.
 */ export function PursuitFactsRail({ header, onLinkContract }) {
    const [rfpOpen, setRfpOpen] = useState(false);
    const { capabilities } = header;
    const sections = [];
    sections.push({
        id: 'pursuit',
        title: 'Pursuit',
        facts: facts([
            {
                id: 'lifecycle',
                label: 'Lifecycle',
                value: lifecycleLabel(header.lifecycle)
            },
            {
                id: 'owner',
                label: 'Owner',
                value: header.owner?.displayName
            },
            {
                id: 'opened',
                label: 'Opened',
                value: header.openedAt ? `${monthYear(header.openedAt)}${header.openedBasis === 'at_award' ? ', at award' : ''}` : ''
            }
        ])
    });
    const opp = header.opportunity;
    const canEditRfp = capabilities.canEdit && !!opp?.opportunityId;
    const rfpValue = monthYear(opp?.rfpExpectedOn ?? '');
    const rfpFact = canEditRfp ? /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex items-center gap-[var(--space-1)]",
        children: [
            rfpValue || /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "Not set"
            }),
            /*#__PURE__*/ _jsx(SpecraIconButton, {
                "data-testid": Testids.pursuitRail.rfpExpectedEdit,
                label: "Edit RFP expected",
                onClick: ()=>setRfpOpen(true),
                size: "sm",
                variant: "ghost",
                children: /*#__PURE__*/ _jsx(Pencil, {
                    "aria-hidden": true,
                    className: "h-3.5 w-3.5"
                })
            })
        ]
    }) : rfpValue;
    sections.push(opp ? {
        id: 'opportunity',
        title: 'Opportunity',
        facts: facts([
            {
                id: 'customer',
                label: 'Customer',
                value: ref(opp.customer)
            },
            {
                id: 'office',
                label: 'Contracting office',
                value: ref(opp.contractingOffice)
            },
            {
                id: 'vehicle',
                label: 'Vehicle',
                value: ref(opp.vehicle)
            },
            {
                id: 'value',
                label: 'Estimated value',
                value: opp.estimatedValueLabel
            },
            {
                id: 'set-aside',
                label: 'Set-aside',
                value: opp.setAside
            },
            {
                id: 'rfp',
                label: opp.rfpExpectedBasis === 'recompete_horizon' ? 'Recompete horizon' : 'RFP expected',
                value: rfpFact
            }
        ])
    } : {
        id: 'opportunity',
        title: 'Opportunity',
        children: /*#__PURE__*/ _jsx(SpecraText, {
            size: "meta",
            tone: "muted",
            children: "Not opened from an opportunity"
        })
    });
    if (header.people.length > 0) {
        sections.push({
            id: 'people',
            title: 'Customer people',
            rows: header.people.map((p)=>({
                    id: p.stakeholderId || p.contactId || p.name,
                    label: p.href ? /*#__PURE__*/ _jsx(SpecraLink, {
                        href: p.href,
                        renderLink: renderRouterLink,
                        children: p.name
                    }) : p.name,
                    value: /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: [
                            p.role,
                            p.touchpointCount ? `${p.touchpointCount} touchpoints` : ''
                        ].filter(Boolean).join(' · ')
                    })
                }))
        });
    }
    if (header.competitors.length > 0) {
        sections.push({
            id: 'competitors',
            title: 'Competitors',
            rows: header.competitors.map((c)=>({
                    id: c.participantId || c.companyProfileId,
                    label: c.href ? /*#__PURE__*/ _jsx(SpecraLink, {
                        href: c.href,
                        renderLink: renderRouterLink,
                        children: c.name
                    }) : c.name,
                    value: /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: c.stanceLabel
                    })
                }))
        });
    }
    const currentContracts = header.related?.currentContracts ?? [];
    const related = facts([
        {
            id: 'siblings',
            label: 'Siblings',
            value: refs(header.related?.siblings)
        },
        {
            id: 'landscapes',
            label: 'Landscapes',
            value: refs(header.related?.landscapes)
        },
        {
            id: 'contracts',
            label: 'Current contracts',
            value: refs(currentContracts)
        }
    ]);
    const linkAction = capabilities.canEdit && currentContracts.length === 0 && onLinkContract ? /*#__PURE__*/ _jsx(SpecraButton, {
        "data-testid": Testids.pursuitRail.linkContract,
        iconLeft: /*#__PURE__*/ _jsx(Link2, {
            "aria-hidden": true,
            className: "h-3.5 w-3.5"
        }),
        onClick: onLinkContract,
        size: "xs",
        variant: "secondary",
        children: "Link contract"
    }) : undefined;
    if (related.length > 0 || linkAction) {
        sections.push({
            id: 'related',
            title: 'Related',
            action: linkAction,
            facts: related,
            // GRW-04 v2.1 (OD-19): the hint switch, once a current contract is linked.
            children: currentContracts.length > 0 ? /*#__PURE__*/ _jsx(GrowthHintControl, {
                canEdit: capabilities.canSetGrowthHint,
                captureProjectId: header.captureProjectId,
                value: header.showGrowthHintToDelivery
            }) : undefined
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraRecordRail, {
                "aria-label": "Pursuit details",
                getSectionTestId: (s)=>RAIL_TESTIDS[s.id],
                sections: sections
            }),
            canEditRfp && opp && /*#__PURE__*/ _jsx(RfpExpectedDialog, {
                onOpenChange: setRfpOpen,
                open: rfpOpen,
                opportunityId: opp.opportunityId
            })
        ]
    });
}
